import { motion } from "framer-motion";
import { Dumbbell, WheatOff, HeartPulse, Bone, Sparkles } from "lucide-react";

const BENEFITS = [
  {
    icon: Dumbbell,
    title: "High Protein",
    desc: "9g of plant protein per serving keeps you fuller, longer.",
    accent: "bg-[#3E7D2B] text-white",
    span: "lg:col-span-2 lg:row-span-2",
    big: true,
  },
  {
    icon: WheatOff,
    title: "Gluten Free",
    desc: "Naturally free of gluten — easy on your gut.",
    accent: "bg-[#F9F9F6] text-[#13240D]",
    span: "",
  },
  {
    icon: HeartPulse,
    title: "Zero Cholesterol",
    desc: "Heart-friendly snacking, every single bite.",
    accent: "bg-[#A7D129]/30 text-[#13240D]",
    span: "",
  },
  {
    icon: Bone,
    title: "Rich in Calcium",
    desc: "Supports stronger bones &amp; better recovery.",
    accent: "bg-[#F2E8D5] text-[#13240D]",
    span: "",
  },
  {
    icon: Sparkles,
    title: "Light & Crunchy",
    desc: "Roast it, season it, snack it. Always satisfying.",
    accent: "bg-[#13240D] text-white",
    span: "",
  },
];

export default function Benefits() {
  return (
    <section
      id="benefits"
      className="relative py-24 md:py-32 bg-[#F9F9F6]"
      data-testid="benefits-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            className="text-xs uppercase tracking-[0.25em] font-bold text-[#3E7D2B]"
          >
            Why It&rsquo;s Good For You
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="font-display font-black text-[#13240D] mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          >
            Tiny seeds.{" "}
            <span className="text-[#3E7D2B]">Massive</span> benefits.
          </motion.h2>
          <p className="mt-5 text-lg text-[#4A5D44] max-w-2xl leading-relaxed">
            Every Ahaario pack is a carefully curated power-snack — crafted to
            fit your active, modern lifestyle without compromise.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-5 md:gap-6">
          {BENEFITS.map((b, idx) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: idx * 0.07 }}
                whileHover={{ y: -6 }}
                className={`relative rounded-3xl p-7 md:p-8 ${b.accent} ${b.span} border border-[#E8EDE6] shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden`}
                data-testid={`benefit-card-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className={`h-12 w-12 rounded-2xl flex items-center justify-center ${
                    b.accent.includes("white") || b.accent.includes("text-white")
                      ? "bg-white/15"
                      : "bg-white"
                  }`}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <h3
                  className={`font-display font-extrabold mt-6 ${
                    b.big ? "text-3xl md:text-4xl lg:text-5xl" : "text-2xl"
                  } leading-tight`}
                >
                  {b.title}
                </h3>
                <p
                  className={`mt-3 ${
                    b.accent.includes("text-white")
                      ? "text-white/80"
                      : "text-[#4A5D44]"
                  } ${b.big ? "text-lg max-w-md" : "text-sm"} leading-relaxed`}
                  dangerouslySetInnerHTML={{ __html: b.desc }}
                />
                {b.big && (
                  <div className="absolute -bottom-10 -right-10 h-44 w-44 rounded-full bg-[#A7D129]/30" />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
